<template>
  <!-- Auth Layout (Login Page) -->
  <div v-if="isAuthRoute" class="min-h-screen bg-dark-900">
    <RouterView />
  </div>

  <!-- Main Layout (Authenticated) -->
  <div v-else-if="authStore.isAuthenticated" class="min-h-screen bg-dark-900 flex">
    <!-- Sidebar -->
    <TheSidebar 
      :collapsed="sidebarCollapsed" 
      @toggle="sidebarCollapsed = !sidebarCollapsed"
    />

    <!-- Main Content -->
    <div 
      class="flex-1 flex flex-col transition-all duration-300"
      :class="sidebarCollapsed ? 'ml-[72px]' : 'ml-[260px]'"
    >
      <!-- Header -->
      <TheHeader />

      <!-- Page Content -->
      <main class="flex-1 p-6 overflow-auto">
        <RouterView v-slot="{ Component }">
          <Transition name="page" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
      </main>
    </div>

    <!-- Global Components -->
    <CommandPalette />
    <Notifications />
  </div>

  <!-- Loading/Redirect state -->
  <div v-else class="min-h-screen bg-dark-900 flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
      <p class="text-dark-400 mt-4">Carregando...</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import TheSidebar from '@/components/layout/TheSidebar.vue'
import TheHeader from '@/components/layout/TheHeader.vue'
import CommandPalette from '@/components/common/CommandPalette.vue'
import Notifications from '@/components/common/Notifications.vue'

const route = useRoute()
const themeStore = useThemeStore()
const authStore = useAuthStore()
const dashboardStore = useDashboardStore()
const sidebarCollapsed = ref(false)

const isAuthRoute = computed(() => route.path === '/login' || route.meta.public)

// Initialize dashboard data when authenticated
watch(() => authStore.isAuthenticated, async (isAuth) => {
  if (isAuth && !isAuthRoute.value) {
    await dashboardStore.fetchMetrics()
  }
}, { immediate: true })

// Keyboard shortcuts
const handleKeydown = (e: KeyboardEvent) => {
  // Toggle sidebar with Cmd/Ctrl + B
  if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
    e.preventDefault()
    sidebarCollapsed.value = !sidebarCollapsed.value
  }
  
  // Toggle theme with Cmd/Ctrl + Shift + L
  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'L') {
    e.preventDefault()
    themeStore.toggleTheme()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  themeStore.initTheme()
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.page-enter-active,
.page-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
